import "@/components/Interface/FormFields/Checkbox/Checkbox.scss";

import clsx from "clsx";
import { ReactNode, useEffect, useRef } from "react";
import {
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";

import SelectedCheckbox from "@/assets/ux/SelectedCheckbox.svg?react";
import UnselectedCheckbox from "@/assets/ux/UnselectedCheckbox.svg?react";
import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import {
  CustomRegisterOptions,
  getFormFieldError,
} from "@/components/Interface/FormFields/FormField/FormField.utils.ts";
import { getNestedValue } from "@/utils/objectUtils";

// TODO: For uncontrolled DeviceTile it's best to spread the register props (and not pass the `register` function, `name` and `options`) into the component like this `<Checkbox {...register(name, options)} />`
export interface CheckboxProps<TFieldValues extends FieldValues> {
  id?: string;
  className?: string;
  labelClassName?: string;
  name: Path<TFieldValues>;
  value?: string | number;
  disabled?: boolean; // TODO: If this can change dynamically, we probably need a controlled component
  /**
   * When `true`, adds the `is-inverted` modifier so the box and label use the
   * `*-inverse` theme tokens (light rendering on a dark surface). Defaults to
   * `false`.
   */
  inverted?: boolean;
  /**
   * Sets the input's `indeterminate` DOM property. This property has no HTML
   * attribute, so it is applied imperatively via a ref rather than through
   * markup.
   */
  indeterminate?: boolean;
  label?: string;
  labelComponent?: ReactNode;
  selectedIcon?: JSX.Element;
  unselectedIcon?: JSX.Element;
  options?: RegisterOptions<TFieldValues>;
  errors: FieldErrors;
  register: UseFormRegister<TFieldValues>;
  key?: any;
}

/**
 * Checkbox bound to `react-hook-form`.
 *
 * The native `<input>` is visually hidden and the box is drawn by the sibling
 * `.checkbox` element, which reacts to the input's pseudo-classes (`:checked`,
 * `:indeterminate`, `:disabled`, `:focus-visible`, `.error`). Colours, spacing
 * and radii are defined in `Checkbox.scss` via the semantic Tailwind theme
 * tokens.
 */
export const Checkbox = <TFormValues extends object>({
  id,
  className,
  labelClassName,
  name,
  value,
  disabled,
  inverted,
  indeterminate,
  label,
  labelComponent,
  selectedIcon = <SelectedCheckbox />,
  unselectedIcon = <UnselectedCheckbox />,
  options = { required: true },
  errors,
  register,
  key,
}: CheckboxProps<TFormValues>) => {
  const uid = id || `${name}-${value}`;

  // Use getNestedValue to safely access nested field errors
  const fieldError = getNestedValue(errors, name);
  const hasError = fieldError !== undefined;
  const errorMessage = getFormFieldError(
    errors,
    name,
    options as CustomRegisterOptions,
  );

  // `register` owns the input ref; keep our own handle so the `indeterminate`
  // DOM property can be set imperatively (see the effect below).
  const { ref: registerRef, ...registerProps } = register(name, options);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <div
      id="checkbox-input-container"
      className={clsx(
        "checkbox-container",
        inverted && "is-inverted",
        className,
      )}
    >
      <div className={clsx("checkbox-row", disabled && "is-disabled")}>
        <div className="input-wrapper">
          <input
            {...registerProps}
            ref={(element) => {
              registerRef(element);
              inputRef.current = element;
            }}
            key={key}
            id={uid}
            className={clsx("min-w-max", hasError && "error")}
            type="checkbox"
            {...(value ? { value: value } : {})}
            disabled={disabled}
            data-testid={uid}
            aria-label={label}
            aria-checked={indeterminate ? "mixed" : undefined}
            aria-invalid={hasError ? "true" : "false"}
          />
          <div className="checkbox">
            <div className="selected">{selectedIcon}</div>
            <div className="unselected">{unselectedIcon}</div>
            <div className="indeterminate" aria-hidden="true">
              <span className="dash" />
            </div>
          </div>
        </div>
        {(label !== undefined || labelComponent !== undefined) && (
          <label className={labelClassName} htmlFor={uid}>
            {label}
            {labelComponent}
          </label>
        )}
      </div>
      {errorMessage && <ErrorMessage message={errorMessage} name={name} />}
    </div>
  );
};
